import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Mail, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { format } from "date-fns";

type Range = "24h" | "7d" | "30d";
type Status = "all" | "sent" | "failed";

const PAGE_SIZE = 50;

const rangeToDate = (r: Range) => {
  const now = new Date();
  const ms = r === "24h" ? 24 * 3600e3 : r === "7d" ? 7 * 86400e3 : 30 * 86400e3;
  return new Date(now.getTime() - ms).toISOString();
};

const EmailLogsManagement = () => {
  const [range, setRange] = useState<Range>("7d");
  const [template, setTemplate] = useState<string>("all");
  const [status, setStatus] = useState<Status>("all");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<any | null>(null);

  const since = useMemo(() => rangeToDate(range), [range]);

  const { data: templates } = useQuery({
    queryKey: ["email-log-templates"],
    queryFn: async () => {
      const { data } = await supabase
        .from("email_logs")
        .select("template_name")
        .limit(1000);
      const set = new Set<string>();
      (data || []).forEach((r: any) => set.add(r.template_name));
      return Array.from(set).sort();
    },
  });

  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ["email-log-stats", range, template, status],
    queryFn: async () => {
      let q = supabase
        .from("email_logs")
        .select("status", { count: "exact" })
        .gte("created_at", since);
      if (template !== "all") q = q.eq("template_name", template);

      const [totalRes, sentRes, failedRes] = await Promise.all([
        q,
        supabase.from("email_logs").select("id", { count: "exact", head: true })
          .gte("created_at", since).eq("status", "sent")
          .then(r => template !== "all"
            ? supabase.from("email_logs").select("id", { count: "exact", head: true })
                .gte("created_at", since).eq("status", "sent").eq("template_name", template)
            : r),
        supabase.from("email_logs").select("id", { count: "exact", head: true })
          .gte("created_at", since).eq("status", "failed")
          .then(r => template !== "all"
            ? supabase.from("email_logs").select("id", { count: "exact", head: true })
                .gte("created_at", since).eq("status", "failed").eq("template_name", template)
            : r),
      ]);
      return {
        total: totalRes.count || 0,
        sent: sentRes.count || 0,
        failed: failedRes.count || 0,
      };
    },
  });

  const { data: logs, isLoading, refetch } = useQuery({
    queryKey: ["email-logs", range, template, status, page],
    queryFn: async () => {
      let q = supabase
        .from("email_logs")
        .select("*", { count: "exact" })
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);
      if (template !== "all") q = q.eq("template_name", template);
      if (status !== "all") q = q.eq("status", status);
      const { data, count } = await q;
      return { rows: data || [], total: count || 0 };
    },
  });

  const totalPages = Math.ceil((logs?.total || 0) / PAGE_SIZE);

  const handleRefresh = () => {
    refetch();
    refetchStats();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Email Logs</h2>
          <p className="text-gray-600 text-sm mt-1">Transactional email send history</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total</CardTitle>
            <Mail className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold">{stats?.total ?? "—"}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Sent</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold text-green-600">{stats?.sent ?? "—"}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold text-red-600">{stats?.failed ?? "—"}</div></CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-3">
            <Select value={range} onValueChange={(v) => { setRange(v as Range); setPage(0); }}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">Last 24h</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
            <Select value={template} onValueChange={(v) => { setTemplate(v); setPage(0); }}>
              <SelectTrigger className="w-56"><SelectValue placeholder="Template" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All templates</SelectItem>
                {(templates || []).map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={(v) => { setStatus(v as Status); setPage(0); }}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : (logs?.rows.length || 0) === 0 ? (
            <div className="text-center py-12 text-gray-500">No emails found for these filters</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Template</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs?.rows.map((row: any) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() => setSelected(row)}
                  >
                    <TableCell className="text-sm whitespace-nowrap">
                      {format(new Date(row.created_at), "MMM d, HH:mm")}
                    </TableCell>
                    <TableCell className="text-sm font-mono">{row.template_name}</TableCell>
                    <TableCell className="text-sm">{row.recipient_email}</TableCell>
                    <TableCell>
                      <Badge variant={row.status === "sent" ? "default" : "destructive"}>
                        {row.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-red-600 max-w-xs truncate">
                      {row.error_message || ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-gray-600">
                Page {page + 1} of {totalPages} · {logs?.total} total
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Email details</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div><span className="text-gray-500">Template:</span> <span className="font-mono">{selected.template_name}</span></div>
              <div><span className="text-gray-500">To:</span> {selected.recipient_email} {selected.recipient_name ? `(${selected.recipient_name})` : ""}</div>
              <div><span className="text-gray-500">Subject:</span> {selected.subject}</div>
              <div><span className="text-gray-500">Status:</span> <Badge variant={selected.status === "sent" ? "default" : "destructive"}>{selected.status}</Badge></div>
              <div><span className="text-gray-500">Sent at:</span> {format(new Date(selected.created_at), "PPpp")}</div>
              {selected.mailersend_message_id && (
                <div><span className="text-gray-500">MailerSend ID:</span> <span className="font-mono text-xs">{selected.mailersend_message_id}</span></div>
              )}
              {selected.error_message && (
                <div>
                  <div className="text-gray-500 mb-1">Error:</div>
                  <pre className="bg-red-50 text-red-800 p-3 rounded text-xs whitespace-pre-wrap">{selected.error_message}</pre>
                </div>
              )}
              {selected.metadata && Object.keys(selected.metadata).length > 0 && (
                <div>
                  <div className="text-gray-500 mb-1">Metadata:</div>
                  <pre className="bg-gray-50 p-3 rounded text-xs whitespace-pre-wrap">{JSON.stringify(selected.metadata, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailLogsManagement;
